import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from '../../app/store'
import {
  checkAuth,
  checkOTPRequirement,
  fetchUserPermissions,
  login,
  logout,
  requestOTPCode,
  signup,
  updateUserPublicFlags,
  verify,
  verifyOTPCode,
} from './api'
import { OTPStatus, SignupData, User, UserPublicFlag } from './types'
import { updateUserData } from '../tools/api'
import { toast } from 'react-toastify'
import { logoutErp } from '../erp/api'
import { captureException } from '@sentry/react'
import { acceptTOS, getCurrentTOS } from '../tos/api'
import { TOSDocument } from '../tos/types'

export class AuthStore {
  rootStore: RootStore
  token: string | undefined
  user: User | undefined

  // Auth loading states
  checkingAuth = false
  requestingOTP = false
  verifyingOTP = false

  otpStatus: OTPStatus | undefined

  get avatarUrl(): string | null {
    return this.user?.avatarUrl || null
  }

  // TOS related properties
  currentTOS: TOSDocument | null = null
  loadingTOS = false

  // Computed property to determine if user needs to review TOS
  get requiresTOSReview(): boolean {
    if (!this.user || !this.currentTOS) {
      return false
    }

    // If the user hasn't accepted TOS yet
    if (!this.user.lastTosAcceptedAt) {
      return true
    }

    // Check if the TOS is newer than the user's last acceptance
    const lastAcceptedDate = new Date(this.user.lastTosAcceptedAt)
    const tosDate = new Date(this.currentTOS.date)

    return tosDate > lastAcceptedDate
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  setUser(user: User | undefined) {
    if (!user) {
      this.user = undefined
      return
    }
    const formattedUser = {
      ...user,
      favoriteTools:
        user.favoriteTools?.map((t: any) => (t.id ? t.id : t)) || [],
    }
    this.user = formattedUser
  }

  public async fetchUserPermissions() {
    const permissionsData = await fetchUserPermissions()
    runInAction(() => {
      if (this.user) {
        this.user.permissions = permissionsData.allPermissions
      }
    })
  }

  public async updateUserData(data: Record<string, any>) {
    await updateUserData(this.user!.id, data)

    runInAction(() => {
      if (!this.user) {
        return
      }

      for (const [key, value] of Object.entries(data)) {
        // @ts-ignore
        this.user[key] = value
      }
    })
  }

  public async login(email: string, password: string) {
    const data = await login(email, password)
    if (data.token) {
      this.token = data.token
      this.setUser(data.user)

      // Fetch user permissions and TOS in parallel to reduce latency
      await Promise.all([this.fetchUserPermissions(), this.fetchCurrentTOS()])
    }

    return data
  }

  public async logout() {
    await Promise.race([
      logoutErp().catch((error) => {
        captureException(error)
        // Shallow error to continue logging out
      }),
      new Promise((resolve) => setTimeout(resolve, 2000)),
    ])

    await logout()
    this.token = undefined
    this.setUser(undefined)

    // Clear TOS information
    runInAction(() => {
      this.currentTOS = null
    })
  }

  public async signup(data: SignupData) {
    const result = await signup(data)
    if (result.token) {
      this.token = result.token
      this.setUser(result.user)
    }

    return result
  }

  public async checkAuth() {
    runInAction(() => {
      this.checkingAuth = true
    })

    try {
      // Fetch auth data first
      const data = await checkAuth()

      if (data.token) {
        this.token = data.token
        this.setUser(data.user)

        await Promise.all([this.fetchCurrentTOS(), this.fetchUserPermissions()])
      } else {
        // Even if not authenticated, try to fetch the TOS
        // This can be helpful for public pages or signup flows
        await this.fetchCurrentTOS().catch((err) => {
          // Silently fail, we don't want to block the flow if TOS can't be fetched
          console.error('Failed to fetch TOS for unauthenticated user:', err)
        })
      }

      return data
    } finally {
      runInAction(() => {
        this.checkingAuth = false
      })
    }
  }

  public async verify(token: string) {
    const data = await verify(token)
    return data
  }

  public async checkOTPRequirement() {
    const data = await checkOTPRequirement()

    runInAction(() => {
      this.otpStatus = data
    })

    return data
  }

  public async requestOTP() {
    this.requestingOTP = true

    const otpStatus = await requestOTPCode()

    if (otpStatus === 'sent') {
      toast.success('Código enviado correctamente.')
    }

    runInAction(() => {
      this.requestingOTP = false
      this.otpStatus = otpStatus
    })
  }

  public async verifyOTP(otp: string) {
    this.verifyingOTP = true

    const otpStatus = await verifyOTPCode(otp)

    runInAction(() => {
      this.otpStatus = otpStatus
      this.verifyingOTP = false
    })
  }

  public async addUserPublicFlag(flag: UserPublicFlag) {
    if (!this.user) {
      return
    }

    return updateUserPublicFlags([flag]).then(() => {
      runInAction(() => {
        this.user!.publicFlags = [...(this.user?.publicFlags || []), flag]
      })
    })
  }

  public async updateUserPublicFlags(flags: UserPublicFlag[]) {
    if (!this.user) {
      return
    }

    return updateUserPublicFlags(flags).then(() => {
      runInAction(() => {
        this.user!.publicFlags = [...(this.user?.publicFlags || []), ...flags]
      })
    })
  }

  // Fetch the current TOS document
  public async fetchCurrentTOS() {
    if (this.loadingTOS) {
      return
    }

    runInAction(() => {
      this.loadingTOS = true
    })

    try {
      const tosDocument = await getCurrentTOS()

      runInAction(() => {
        this.currentTOS = tosDocument
        this.loadingTOS = false
      })

      return tosDocument
    } catch (error) {
      runInAction(() => {
        this.loadingTOS = false
      })

      console.error('Error fetching TOS:', error)
      return null
    }
  }

  // Accept the current TOS
  public async acceptTOS() {
    if (!this.user || !this.currentTOS) {
      return false
    }

    try {
      await acceptTOS()

      // Update the user's lastTosAcceptedAt in the store
      runInAction(() => {
        if (this.user) {
          this.user.lastTosAcceptedAt = new Date().toISOString()
        }
      })

      return true
    } catch (error) {
      console.error('Error accepting TOS:', error)
      return false
    }
  }
}
